/**
 * Shared helper for the customer + vehicle delete flows.
 *
 * Both detail pages parse the same DELETE error envelope and build the
 * same pluralised "still has N appointment(s) and M work order(s)"
 * blocker string — this centralises that so the two call sites don't
 * drift.
 */

/** Loose translator type — both pages pass their namespaced i18next `t`
 * (customers / vehicles); both namespaces expose the same
 * `detail.delete.*` keys. */
type Translate = (key: string, options?: Record<string, unknown>) => string

export type DeleteErrorBody = {
  error?: string
  dependents?: { appointments?: number; jobs?: number }
} | null

/**
 * Map a failed DELETE response into a user-facing message. For a
 * `409 has_dependents` it returns the pluralised blocker string; returns
 * `null` for any other failure so the caller falls back to a generic
 * message.
 */
export function deleteBlockerMessage(
  t: Translate,
  status: number,
  body: DeleteErrorBody,
): string | null {
  if (status !== 409 || body?.error !== 'has_dependents') return null
  const d = body.dependents ?? {}
  const parts: string[] = []
  if ((d.appointments ?? 0) > 0)
    parts.push(t('detail.delete.dep_appointments', { count: d.appointments }))
  if ((d.jobs ?? 0) > 0)
    parts.push(t('detail.delete.dep_jobs', { count: d.jobs }))
  return t('detail.delete.blocked', {
    dependents: parts.join(t('detail.delete.dep_join')),
  })
}
